/**
 * 画面スリープ防止（Screen Wake Lock API）。
 * - タブが非表示になると OS が自動解除するので、visibilitychange で取り直す。
 * - iOS はバージョンやホーム画面起動（PWA）かどうかで挙動差があるため、状態を診断パネルに出す。
 */
export type WakeLockStatus = 'unsupported' | 'released' | 'active' | 'error';

export class WakeLockKeeper {
  private sentinel: WakeLockSentinel | null = null;
  private retry = 0;
  private wanted = false;
  private listeners = new Set<(s: WakeLockStatus, detail?: string) => void>();
  status: WakeLockStatus = 'wakeLock' in navigator ? 'released' : 'unsupported';

  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (this.wanted && document.visibilityState === 'visible') void this.acquire();
    });
  }

  onChange(fn: (s: WakeLockStatus, detail?: string) => void): void {
    this.listeners.add(fn);
  }

  /** ユーザー操作（タップ）のハンドラ内から呼ぶこと */
  async enable(): Promise<void> {
    this.wanted = true;
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.wanted = false;
    await this.sentinel?.release();
    this.sentinel = null;
    this.set('released');
  }

  private async acquire(): Promise<void> {
    if (!('wakeLock' in navigator)) return this.set('unsupported');
    try {
      this.sentinel = await navigator.wakeLock.request('screen');
      this.sentinel.addEventListener('release', () => {
        this.set('released');
        // 表示中なのに解除された（Android の省電力モードなど OS 側の都合）ときは、少し待って取り直す
        if (this.wanted && document.visibilityState === 'visible') setTimeout(() => void this.acquire(), 1000);
      });
      this.set('active');
    } catch (e) {
      // Android の省電力モード（バッテリーセーバー）では拒否される。表示中なら 30 秒ごとに取り直す
      this.set('error', e instanceof Error ? e.message : String(e));
      clearTimeout(this.retry);
      this.retry = window.setTimeout(() => {
        if (this.wanted && document.visibilityState === 'visible' && this.status !== 'active') void this.acquire();
      }, 30_000);
    }
  }

  private set(s: WakeLockStatus, detail?: string) {
    this.status = s;
    this.listeners.forEach((fn) => fn(s, detail));
  }
}
